import React, { Component } from 'react'
import '../css/basemap-widget.css'
import '../css/scalebar-widget.css'
import styled from 'styled-components'
import IEventedComponent, { messageTypes } from '../components/IEventedComponent'
import mapConfig from '../config/map-config'
import MapLoadingSpinner from '../components/map-loading-spinner'
import SidebarToggleControl from './sidebar-toggle-control'
import MapLegendControl from './map-legend-control'
import MapWarningControl from './map-warning-control'
import { buildSearch } from '../utils/searchUtils'
import { hexToRgb } from '../utils/colourUtils'
import colours from '../lib/colours'
import {
  ParcelLevyLiabilitiesRequestedEvent,
  ParcelLevyLiabilitiesReceivedEvent,
  ParcelLevyLiabilitiesErrorEvent,
  ParcelRemovedEvent,
  ParcelReaddedEvent,
  AreaLevyLiabilitiesReceivedEvent,
  AreaLevyLiabilitiesRequestedEvent,
  AreaLevyLiabilitiesErrorEvent,
  DisableParcelSelectionEvent,
  EnableParcelSelectionEvent,
  EnableParcelSelectionModeEvent,
  UploadAreaAddedEvent
} from '../lib/messages'
import { toTitleCase } from '../utils/otherUtils'
import {
  calculateLevyLiabilities,
  createBasemaps,
  createMapImageLayers,
  createSimplePolygonRenderer,
  getLayerConfig,
  getLayerIdFromName,
  getSimpleFillSymbol,
  getSimpleMarkerSymbol,
  projectGeometry,
  queryFeatures,
  filterZMCoordinates
} from '../utils/mapUtils'
import NvraAreaWarning from '../content/nvra-area-click-warning.mdx'
import { MobileDeviceWidth } from '../lib/constants'

declare global {
  interface Window {
    require: (items: string[], callback: (...items: any) => void) => void
  }
}

const esriLibrary: any = {}

const MapContainer = styled.div`
  width: 100%;
  height: 100%;
  @media (max-width: ${MobileDeviceWidth}px) {
    flex-basis: unset;
    height: 40%;
  }
`

const MapDiv = styled.div`
  width: 100%;
  height: 100%;
`

interface IProps {
  parcelsSelected: string[]
  parcelData: any
  searchLocation: any
  showOutsideMsaWarning: boolean
  setShowOutsideMsaWarning: any
  setInfoPopupState: any
}

interface IState {
  mapViewUpdating: boolean
  isSidebarOpen: boolean
  isLegendOpen: boolean
  legendItems: any[]
  mapScale: number
  isParcelSelectionModeEnabled: boolean
  isParcelSelectionEnabled: boolean
}

const MapControl = class extends IEventedComponent<IProps, IState> {
  map: any
  mapView: any
  sr: any
  processingSr: any
  initialExtent: any
  mapViewUpdatingWatchHandle: any
  mapViewImmediateClickWatchHandle: any
  mapViewPointerMoveWatchHandle: any
  mapSpinnerTimeout: any
  legendConfigs: any = {}
  searchWidget: any
  suggestResults: any[] = []
  graphicsLayers: any = {
    selectedParcels: null,
    highlightedParcel: null,
    searchLocation: null
  }
  geojsonLayers: any = {
    uploadedShapefile: null
  }
  pointerMoveTimeout: any

  state = {
    mapViewUpdating: false,
    isSidebarOpen: true,
    isLegendOpen: false,
    legendItems: [],
    mapScale: 0,
    isParcelSelectionModeEnabled: true,
    isParcelSelectionEnabled: true
  }

  componentDidMount() {
    this.initMap()
    this.subscribe(messageTypes.onParcelRemoved, this.handleParcelRemoved.bind(this))
    this.subscribe(messageTypes.onShapefileUploaded, this.handleShapefileUploaded.bind(this))
    this.subscribe(messageTypes.onDisableParcelSelectionMode, () =>
      this.setState({
        isParcelSelectionModeEnabled: false,
        isParcelSelectionEnabled: false
      })
    )
    this.subscribe(messageTypes.onEnableParcelSelectionMode, () =>
      this.setState({ isParcelSelectionModeEnabled: true })
    )
    this.subscribe(messageTypes.onDisableParcelSelection, () => this.setState({ isParcelSelectionEnabled: false }))
    this.subscribe(messageTypes.onEnableParcelSelection, () => this.setState({ isParcelSelectionEnabled: true }))
    this.subscribe(messageTypes.onUploadAreaRemoved, this.handleUploadAreaRemoved.bind(this))
    this.subscribe(messageTypes.onParcelMouseEnter, this.highlightParcelFromSpi.bind(this))
    this.subscribe(messageTypes.onParcelMouseLeave, this.dehighlightParcels.bind(this))
  }

  componentWillUnmount() {
    clearTimeout(this.mapSpinnerTimeout)
  }

  componentDidUpdate(prevProps: any, prevState: any) {
    if (prevProps.searchLocation?.searchTerm !== this.props.searchLocation?.searchTerm) {
      if (this.searchWidget) this.searchWidget.searchTerm = this.props.searchLocation?.searchTerm
      if (esriLibrary.Point) this.setInitialMapView()
    }
    if (prevState.isParcelSelectionModeEnabled && !this.state.isParcelSelectionModeEnabled) {
      this.disableParcelSelectionMode()
    } else if (!prevState.isParcelSelectionModeEnabled && this.state.isParcelSelectionModeEnabled) {
      this.enableParcelSelectionMode()
    } else if (prevState.isParcelSelectionEnabled && !this.state.isParcelSelectionEnabled) {
      this.disableParcelSelection()
    } else if (!prevState.isParcelSelectionEnabled && this.state.isParcelSelectionEnabled) {
      this.enableParcelSelection()
    }
  }

  setInitialMapView() {
    if (this.props.searchLocation?.coords) {
      if (this.graphicsLayers.searchLocation) this.displaySearchLocation()
    }
  }

  initMap = () => {
    let component: any = this
    window.require(
      [
        'esri/Map',
        'esri/views/MapView',
        'esri/layers/TileLayer',
        'esri/layers/WMTSLayer',
        'esri/layers/MapImageLayer',
        'esri/Basemap',
        'esri/geometry/SpatialReference',
        'esri/geometry/Extent',
        'esri/widgets/Search',
        'esri/tasks/Locator',
        'esri/Graphic',
        'esri/geometry/Point',
        'esri/layers/GraphicsLayer',
        'esri/geometry/projection',
        'esri/geometry/geometryEngine',
        'esri/tasks/support/FeatureSet',
        'esri/layers/GeoJSONLayer',
        'esri/widgets/ScaleBar',
        'esri/widgets/BasemapGallery',
        'esri/widgets/Expand'
      ],
      function (
        Map: any,
        MapView: any,
        TileLayer: any,
        WMTSLayer: any,
        MapImageLayer: any,
        Basemap: any,
        SpatialReference: any,
        Extent: any,
        Search: any,
        Locator: any,
        Graphic: any,
        Point: any,
        GraphicsLayer: any,
        projection: any,
        geometryEngine: any,
        FeatureSet: any,
        GeoJSONLayer: any,
        ScaleBar: any,
        BasemapGallery: any,
        Expand: any
      ) {
        esriLibrary.TileLayer = TileLayer
        esriLibrary.WMTSLayer = WMTSLayer
        esriLibrary.Basemap = Basemap
        esriLibrary.SpatialReference = SpatialReference
        esriLibrary.Extent = Extent
        esriLibrary.Search = Search
        esriLibrary.Locator = Locator
        esriLibrary.Graphic = Graphic
        esriLibrary.Point = Point
        esriLibrary.GraphicsLayer = GraphicsLayer
        esriLibrary.projection = projection
        esriLibrary.geometryEngine = geometryEngine
        esriLibrary.FeatureSet = FeatureSet
        esriLibrary.GeoJSONLayer = GeoJSONLayer
        esriLibrary.ScaleBar = ScaleBar
        esriLibrary.BasemapGallery = BasemapGallery
        esriLibrary.Expand = Expand

        const basemaps = createBasemaps(esriLibrary)

        component.map = new Map({
          basemap: basemaps[0]
        })
        component.sr = new SpatialReference({ wkid: 3111 })
        component.processingSr = new SpatialReference({ wkid: 4283 })
        component.mapView = new MapView({
          container: 'map',
          map: component.map,
          spatialReference: component.sr,
          extent: new Extent({
            ...mapConfig.initialExtent,
            spatialReference: component.sr
          }),
          constraints: {
            snapToZoom: false
          }
        })

        createMapImageLayers(MapImageLayer, component.legendConfigs).then(
          (mapImageLayers: any) => {
            component.map.addMany(mapImageLayers)
            component.createLegendItems()
            component.addGraphicsLayers(esriLibrary)
            component.setInitialMapView()
          },
          (reasons: any) => {
            throw new Error(`There was a problem creating the map image layers`)
          }
        )

        component.addMapViewUiComponents(basemaps)

        component.searchWidget = buildSearch('mapSearchWidgetInput', esriLibrary, component.view)
        if (component.props.searchLocation?.searchTerm) {
          component.searchWidget.search(component.props.searchLocation.searchTerm)
        }

        component.searchWidget.when(() => {
          const element = document.getElementById('mapSearchWidgetInput')
          if (element) {
            element.childNodes[0].childNodes[2].childNodes[0].childNodes[0].onkeydown = (event: any) => {
              if (event.keyCode === 13 && component.suggestResults.length > 0) {
                event.preventDefault()
                component.searchWidget.search(component.suggestResults[0].text)
              }
            }
          }
        })

        component.searchWidget.on('suggest-start', () => {
          component.suggestResults = []
        })

        // Prevent default when search scroll bar clicked
        component.searchWidget.on('suggest-complete', (evt: any) => {
          component.suggestResults = evt.results[0].results
          const element = document.getElementById('mapSearchWidgetInput')
          if (element) {
            const childNode: any = element.childNodes[0].childNodes[2].childNodes[1]
            childNode.onmousedown = (event: any) => event.preventDefault()
          }
        })

        component.searchWidget.on('select-result', (result: any) => {
          const { feature, name } = result.result
          const coords = [feature.geometry.longitude, feature.geometry.latitude]
          component.publish(messageTypes.onSearchLocationChanged, {
            searchTerm: name,
            coords
          })
        })

        component.mapViewUpdatingWatchHandle = component.mapView.watch('updating', (updating: boolean) => {
          if (!updating) {
            // When layers update, often the spinner gets displayed and hidden quickly twice which is distracting
            // This timeout should prevent this occurring.
            component.mapSpinnerTimeout = setTimeout(() => {
              component.setState((state: any, props: any) => ({
                mapViewUpdating: state.mapViewUpdating && updating
              }))
            }, 200)
          } else {
            component.setState({
              mapViewUpdating: updating
            })
          }
        })

        component.mapView.watch('stationary', (event: any) => {
          component.setState({ mapScale: component.mapView.scale })
          component.updateParcelSelection()
        })

        component.enableParcelSelection()
      }
    )
  }

  updateParcelSelection = () => {
    if (
      this.mapView.scale > mapConfig.parcelSelectionScale &&
      this.state.isParcelSelectionModeEnabled &&
      this.state.isParcelSelectionEnabled
    ) {
      this.publish<DisableParcelSelectionEvent>(messageTypes.onDisableParcelSelection, {})
    } else if (
      this.mapView.scale <= mapConfig.parcelSelectionScale &&
      this.state.isParcelSelectionModeEnabled &&
      !this.state.isParcelSelectionEnabled
    ) {
      this.publish<EnableParcelSelectionEvent>(messageTypes.onEnableParcelSelection, {})
    }
  }

  enableParcelSelection = () => {
    let component = this
    component.mapViewPointerMoveWatchHandle = component.mapView.on('pointer-move', (event: any) => {
      if (component.pointerMoveTimeout) {
        clearTimeout(component.pointerMoveTimeout)
      }
      component.pointerMoveTimeout = setTimeout(() => {
        const mapPoint = component.mapView.toMap({ x: event.x, y: event.y })
        component.handleMapHover(mapPoint)
      }, 200)
    })
    component.mapViewImmediateClickWatchHandle = component.mapView.on('immediate-click', (event: any) => {
      const mapPoint = this.mapView.toMap({ x: event.x, y: event.y })
      component.toggleSelectParcelOnMap(mapPoint)
    })
  }

  disableParcelSelection = () => {
    if (this.graphicsLayers.highlightedParcel) this.dehighlightParcels()
    this.mapView.surface.style.cursor = 'default'
    if (this.mapViewPointerMoveWatchHandle) this.mapViewPointerMoveWatchHandle.remove()
    if (this.mapViewImmediateClickWatchHandle) this.mapViewImmediateClickWatchHandle.remove()
  }

  enableParcelSelectionMode = () => {
    this.updateParcelSelection()
  }

  disableParcelSelectionMode = () => {
    this.disableParcelSelection()
  }

  addGraphicsLayers = () => {
    Object.keys(this.graphicsLayers).forEach((graphicsLayerKey: string) => {
      this.graphicsLayers[graphicsLayerKey] = new esriLibrary.GraphicsLayer({})
      this.map.add(this.graphicsLayers[graphicsLayerKey])
    })
  }

  createLegendItems = () => {
    let legendItems: any = []
    Object.keys(this.legendConfigs).forEach((legendConfigKey: any) => {
      const legendConfig = this.legendConfigs[legendConfigKey]
      legendConfig.layers.forEach((legendLayerConfig: any) => {
        const layerConfig = getLayerConfig(legendConfigKey, legendLayerConfig.layerName)
        if (layerConfig.legendGroup && layerConfig.legendOrder) {
          let legendItem = {
            mapServiceId: legendConfigKey,
            layerId: legendLayerConfig.layerId,
            layerName: legendLayerConfig.layerName,
            legendGroup: layerConfig.legendGroup,
            legendOrder: layerConfig.legendOrder,
            visible: this.isLegendItemVisible(legendConfigKey, legendLayerConfig.layerId),
            minScale: legendLayerConfig.minScale,
            maxScale: legendLayerConfig.maxScale,
            swatch: '',
            secondaryItems: []
          }
          if (legendLayerConfig.legend.length === 1) {
            legendItem.swatch = `data: ${legendLayerConfig.legend[0].contentType};base64,${legendLayerConfig.legend[0].imageData}`
          } else {
            legendItem.secondaryItems = legendLayerConfig.legend.map((x: any) => {
              return {
                label: x.label,
                swatch: `data: ${x.contentType};base64,${x.imageData}`
              }
            })
          }
          legendItems.push(legendItem)
        }
      })
    })
    this.setState({ legendItems: legendItems })
  }

  addMapViewUiComponents = (basemaps: any[]) => {
    let component = this

    component.mapView.ui.add(
      new esriLibrary.ScaleBar({
        view: component.mapView,
        unit: 'metric'
      }),
      {
        position: 'bottom-right'
      }
    )

    component.mapView.ui.add(
      new esriLibrary.Expand({
        view: component.mapView,
        autoCollapse: true,
        content: new esriLibrary.BasemapGallery({
          view: component.mapView,
          source: basemaps
        })
      }),
      'top-right',
      1
    )
  }

  getSublayer(mapServiceId: string, layerId: number): any {
    const layer = this.map.findLayerById(mapServiceId)
    const sublayer = layer.sublayers.getItemAt(layer.sublayers.findIndex((x: any) => x.id === layerId))
    if (!sublayer) {
      throw new Error(`Unable to get sublayer for map service: ${mapServiceId} and layerId: ${layerId}`)
    }
    return sublayer
  }

  findEmisMsaSubLayer = (subLayer: string) => {
    return this.getSublayer(
      'emisMsa',
      getLayerIdFromName(this.legendConfigs.emisMsa, mapConfig.dynamicMapServices.emisMsa.layers[subLayer].name)
    )
  }

  isLegendItemVisible(mapServiceId: string, layerId: number): boolean {
    return this.getSublayer(mapServiceId, layerId).visible
  }

  toggleLayerVisibility = (legendItem: any) => {
    this.setState(prevState => {
      let legendItems = [...prevState.legendItems]
      legendItems.forEach(x => {
        if (x.layerId === legendItem.layerId) {
          x.visible = !x.visible
        }
      })
      return { legendItems }
    })

    const sublayer = this.getSublayer(legendItem.mapServiceId, legendItem.layerId)
    sublayer.visible = !sublayer.visible
  }

  async checkWithinNativeVegRegulationsArea(geometry: any) {
    const nativeVegRegulationsArea: any = this.findEmisMsaSubLayer('nativeVegetationRegulationsArea')
    return queryFeatures(nativeVegRegulationsArea.url, geometry, '1=1', ['objectid'], false).then(
      (features: any) => {
        return !!features.length
      },
      (reason: any) => {
        console.log(reason)
      }
    )
  }

  displaySearchLocation() {
    const geom = new esriLibrary.Point({
      x: this.props.searchLocation.coords[0],
      y: this.props.searchLocation.coords[1]
    })
    projectGeometry(esriLibrary, geom, this.sr).then((projectedGeometry: any) => {
      const pointGraphic = new esriLibrary.Graphic(projectedGeometry)
      pointGraphic.symbol = getSimpleMarkerSymbol(colours.aqua)
      this.graphicsLayers.searchLocation.removeAll()
      this.graphicsLayers.searchLocation.add(pointGraphic)
      this.mapView.goTo({ target: pointGraphic, scale: 5000 }, { duration: 800 })
      this.checkWithinNativeVegRegulationsArea(pointGraphic.geometry).then((result: boolean | void) => {
        this.props.setShowOutsideMsaWarning(!!result)
      })
    })
  }

  findSelectedParcelFromPoint(mapPoint: any) {
    const parcelGraphics = this.graphicsLayers.selectedParcels.graphics
    const graphicIndex = parcelGraphics.findIndex((g: any) => g.geometry.contains(mapPoint))
    if (graphicIndex !== -1) return parcelGraphics.items[graphicIndex]
    return null
  }

  findSelectedParcelFromSpi(parcelSpi: string) {
    const parcelGraphics = this.graphicsLayers.selectedParcels.graphics
    const graphicIndex = parcelGraphics.findIndex((g: any) => g.attributes.PARCEL_SPI === parcelSpi)
    if (graphicIndex !== -1) return parcelGraphics.items[graphicIndex]
    return null
  }

  async getBackendParcelFromPoint(mapPoint: any) {
    const parcelLayer: any = this.getSublayer(
      'emisMsa',
      getLayerIdFromName(this.legendConfigs.emisMsa, mapConfig.dynamicMapServices.emisMsa.layers.parcels.name)
    )
    const parcelQueryFields = ['objectid', 'PARCEL_PFI', 'PARCEL_SPI']
    return queryFeatures(parcelLayer.url, mapPoint, '', parcelQueryFields).then(
      (features: any) => {
        return features && features.length && features[0]
      },
      (reason: any) => {
        console.log(reason)
      }
    )
  }

  addParcelToSelection(parcelFeature: any) {
    const aquaRgb = hexToRgb(colours.aqua)
    const parcelGraphic = new esriLibrary.Graphic({
      geometry: parcelFeature.geometry,
      attributes: parcelFeature.attributes,
      symbol: getSimpleFillSymbol([aquaRgb?.r, aquaRgb?.g, aquaRgb?.b, 0.1], colours.aqua, 5)
    })
    this.graphicsLayers.selectedParcels.add(parcelGraphic)
    this.fetchParcelDetails(parcelGraphic.attributes)
  }

  async toggleSelectParcelOnMap(mapPoint: any) {
    const { parcelsSelected } = this.props
    const parcelGraphic = this.findSelectedParcelFromPoint(mapPoint)
    if (parcelGraphic) {
      if (parcelsSelected.includes(parcelGraphic.attributes.PARCEL_SPI))
        this.publish<ParcelRemovedEvent>(messageTypes.onParcelRemoved, {
          parcelSpi: parcelGraphic.attributes.PARCEL_SPI
        })
      return
    }

    const [withinNvArea] = await Promise.all([this.checkWithinNativeVegRegulationsArea(mapPoint)])
    if (withinNvArea) {
      this.props.setInfoPopupState(true, NvraAreaWarning)
      return
    }

    const backendParcel = await this.getBackendParcelFromPoint(mapPoint)
    this.addParcelToSelection(backendParcel)
  }

  handleParcelRemoved(parcelDetails: any) {
    const graphicIndex = this.graphicsLayers.selectedParcels.graphics.findIndex(
      (g: any) => g.attributes.PARCEL_SPI === parcelDetails.parcelSpi
    )
    this.graphicsLayers.selectedParcels.graphics.removeAt(graphicIndex)
    this.dehighlightParcels()
  }

  highlightParcel(geometry: any) {
    this.dehighlightParcels()
    const parcelGraphic = new esriLibrary.Graphic({
      geometry: geometry,
      symbol: getSimpleFillSymbol(colours.darkPurple, colours.darkPurple, 2, 'none', 'short-dash')
    })
    this.graphicsLayers.highlightedParcel.add(parcelGraphic)
  }

  dehighlightParcels() {
    this.graphicsLayers.highlightedParcel.removeAll()
  }

  async handleMapHover(mapPoint: any) {
    let feature = this.findSelectedParcelFromPoint(mapPoint)
    if (!feature) feature = await this.getBackendParcelFromPoint(mapPoint)
    if (feature) {
      this.mapView.surface.style.cursor = 'pointer'
    } else {
      this.mapView.surface.style.cursor = 'default'
    }
    this.highlightParcel(feature.geometry)
  }

  async highlightParcelFromSpi(parcelDetails: any) {
    let feature = this.findSelectedParcelFromSpi(parcelDetails.parcelSpi)
    this.highlightParcel(feature.geometry)
  }

  handleShapefileUploaded(results: any) {
    const component = this
    const geojson = results.geojson
    const blob = new Blob([JSON.stringify(geojson)], {
      type: 'application/json'
    })
    const url = URL.createObjectURL(blob)
    if (!component.geojsonLayers.uploadedShapfile) {
      component.map.remove(component.geojsonLayers.uploadedShapefile)
    }
    const aquaRgb = hexToRgb(colours.aqua)
    component.geojsonLayers.uploadedShapefile = new esriLibrary.GeoJSONLayer({
      url: url,
      renderer: createSimplePolygonRenderer([aquaRgb?.r, aquaRgb?.g, aquaRgb?.b, 0.1], colours.aqua, 5)
    })

    component.map.add(component.geojsonLayers.uploadedShapefile)
    component.geojsonLayers.uploadedShapefile.when(() => {
      component.zoomToUploadedShapefileExtent()
      component.getUploadedAreaJson().then((areaJson: JSON) => {
        component.getAreaLevyLiabilities(areaJson)
        this.publish<UploadAreaAddedEvent>(messageTypes.onUploadAreaAdded, { areaJson: areaJson })
      })
    })
  }

  handleUploadAreaRemoved() {
    this.map.remove(this.geojsonLayers.uploadedShapefile)
    this.geojsonLayers.uploadedShapefile.destroy()
    this.publish<EnableParcelSelectionModeEvent>(messageTypes.onEnableParcelSelectionMode)
  }

  zoomToUploadedShapefileExtent() {
    const component = this
    component.geojsonLayers.uploadedShapefile.queryExtent().then((response: any) => {
      const expandedExtent = response.extent.expand(1.2)
      projectGeometry(esriLibrary, expandedExtent, component.sr).then((projectedGeometry: any) => {
        component.mapView.goTo(projectedGeometry)
      })
    })
  }

  getUploadedAreaJson() {
    const component = this
    return component.geojsonLayers.uploadedShapefile.queryFeatures().then((response: any) => {
      const unionedGeometry = esriLibrary.geometryEngine.union(response.features.map((f: any) => f.geometry))
      return projectGeometry(esriLibrary, unionedGeometry, component.processingSr).then((projectedGeometry: any) => {
        const fs = new esriLibrary.FeatureSet({
          features: [new esriLibrary.Graphic({ geometry: projectedGeometry })]
        })
        return filterZMCoordinates(fs.toJSON())
      })
    })
  }

  getAreaLevyLiabilities(areaJson: JSON) {
    const component = this

    component.publish<AreaLevyLiabilitiesRequestedEvent>(messageTypes.onAreaLevyLiabilitiesRequested, {})
    calculateLevyLiabilities(
      mapConfig.geoprocessingServices.calculateLevyLiabilities.url,
      undefined,
      JSON.stringify(areaJson)
    ).then(
      (results: any) => {
        component.publish<AreaLevyLiabilitiesReceivedEvent>(messageTypes.onAreaLevyLiabilitiesReceived, {
          results: results
        })
      },
      (error: any) => {
        component.publish<AreaLevyLiabilitiesErrorEvent>(messageTypes.onAreaLevyLiabilitiesError, { error: error })
      }
    )
  }

  fetchParcelDetails(parcelAttributes: any) {
    const parcelSpi = parcelAttributes.PARCEL_SPI
    const parcelPfi = parcelAttributes.PARCEL_PFI
    if (parcelSpi in this.props.parcelData) {
      this.publish<ParcelReaddedEvent>(messageTypes.onParcelReadded, {
        parcelSpi: parcelSpi
      })
      return
    }
    this.publish<ParcelLevyLiabilitiesRequestedEvent>(messageTypes.onParcelLevyLiabilitiesRequested, {
      parcelSpi: parcelSpi
    })
    const addressTableUrl = `${mapConfig.dynamicMapServices.parcelAddress.url}/${mapConfig.dynamicMapServices.parcelAddress.tables.parcelAddress.layerId}`
    Promise.all([
      calculateLevyLiabilities(mapConfig.geoprocessingServices.calculateLevyLiabilities.url, parcelSpi),
      queryFeatures(addressTableUrl, null, `PARCEL_PFI='${parcelPfi}'`, ['EZI_ADDRESS'])
    ]).then(
      (results: any) => {
        const parcelData = results[0]
        parcelData.address = results[1].length ? toTitleCase(results[1][0].attributes.EZI_ADDRESS) : ''
        this.publish<ParcelLevyLiabilitiesReceivedEvent>(messageTypes.onParcelLevyLiabilitiesReceived, {
          parcelSpi: parcelSpi,
          parcelData
        })
      },
      (error: any) => {
        this.publish<ParcelLevyLiabilitiesErrorEvent>(messageTypes.onParcelLevyLiabilitiesError, {
          parcelSpi: parcelSpi,
          error: error
        })
      }
    )
  }

  render() {
    return (
      <MapContainer className="col">
        <SidebarToggleControl />
        {this.state.isParcelSelectionModeEnabled && !this.state.isParcelSelectionEnabled ? (
          <MapWarningControl text="Zoom in to select parcels" type="warning" />
        ) : null}
        <MapLegendControl
          legendItems={this.state.legendItems}
          toggleLayerVisibility={this.toggleLayerVisibility}
          mapScale={this.state.mapScale}
        />
        <MapDiv id="map">
          <MapLoadingSpinner visible={this.state.mapViewUpdating} />
        </MapDiv>
      </MapContainer>
    )
  }
}

export default MapControl
